import { qz } from "../client";
import { getSnapshot as getConnectionStoreSnapshot } from "../connection";
import { createNamespaceMutation } from "../internal/createNamespaceMutation";
import { retryOnTransientError } from "./retry";
import {
  markPrinterStatusRefresh,
  printerStore,
  startPrinterListeningState,
  stopPrinterListeningState,
} from "./state";
import {
  type ClearPrinterQueueInput,
  type PrinterListeningOptions,
  type StartPrinterListeningInput,
} from "./types";

/**
 * Normalizes listener input to the shape the JS client forwards as
 * `{ printerNames: [...] }`. Per `StatusMonitor.startListening`, a wire array
 * where `isNull(0)` is true means "listen to every printer", so we collapse
 * `null` / `[]` / `[null]` to a single `null` sentinel in local state.
 */
const normalizeListeningPrinters = (
  printers: StartPrinterListeningInput["printers"],
) => {
  if (printers === null || printers === undefined) return null;

  const list = Array.isArray(printers) ? printers : [printers];
  const filtered = list.filter(
    (name): name is string => typeof name === "string" && name.trim() !== "",
  );
  return filtered.length === 0 ? null : filtered;
};

const normalizeListeningOptions = (
  options: StartPrinterListeningInput["options"],
): PrinterListeningOptions | null => {
  if (!options) return null;

  const normalized: PrinterListeningOptions = {};
  if (options.jobData !== null && options.jobData !== undefined)
    normalized.jobData = options.jobData;
  if (options.maxJobData !== null && options.maxJobData !== undefined)
    normalized.maxJobData = options.maxJobData;
  if (options.flavor !== undefined) normalized.flavor = options.flavor;
  return Object.keys(normalized).length > 0 ? normalized : null;
};

/**
 * Normalizes the union input into the `{ printerName, jobId? }` shape the
 * Java server expects, and rejects missing / blank printer names client-side
 * (the server would otherwise throw `JSONException` on `getString`).
 */
const normalizeClearQueueInput = (
  input: ClearPrinterQueueInput,
): {
  jobId?: number;
  printerName: string;
} => {
  const normalized =
    typeof input === "string" ? { printerName: input } : { ...input };
  if (!normalized.printerName || !normalized.printerName.trim())
    throw new Error("clearQueue requires a non-empty printerName");

  return normalized;
};

export const clearQueueMutationOptions = createNamespaceMutation({
  key: ["qz.printers.clearQueue"],
  run: (input: ClearPrinterQueueInput) => {
    const options = normalizeClearQueueInput(input);
    // Upstream `ClearQueueOptions` marks `printerName` optional incorrectly;
    // we've validated it above, so cast through the loose type to satisfy
    // the runtime signature.
    return qz.printers.clearQueue(options as unknown as never);
  },
  store: printerStore,
});

export const startPrinterListeningMutationOptions = createNamespaceMutation({
  key: ["qz.printers.startListening"],
  run: async ({
    options = null,
    printers = null,
  }: StartPrinterListeningInput = {}) => {
    const normalizedPrinters = normalizeListeningPrinters(printers);
    const normalizedOptions = normalizeListeningOptions(options);
    // Upstream `@types/qz-tray` narrows `flavor` to `"base64"|"hex"|"plain"`
    // and predates some options the Java server accepts. We validated the
    // shape ourselves against `StatusMonitor.java`, so cast through.
    await retryOnTransientError(() =>
      qz.printers.startListening(
        normalizedPrinters,
        (normalizedOptions ?? undefined) as unknown as never,
      ),
    );
    startPrinterListeningState(normalizedPrinters, normalizedOptions);
    await retryOnTransientError(() => qz.printers.getStatus());
    markPrinterStatusRefresh();
  },
  store: printerStore,
});

export const stopPrinterListeningMutationOptions = createNamespaceMutation({
  key: ["qz.printers.stopListening"],
  run: async () => {
    if (getConnectionStoreSnapshot().connected)
      await qz.printers.stopListening();

    stopPrinterListeningState();
  },
  store: printerStore,
});

/**
 * `printers.getStatus` rejects server-side with
 * `"No printer listeners started for this client."` when the current socket
 * session isn't listening (see `PrintSocketClient.processMessage` →
 * `PRINTERS_GET_STATUS`). Guard on client state before dispatching so the
 * common "refresh before listening" interaction is a no-op instead of a
 * rejected promise.
 */
export const refreshPrinterStatusMutationOptions = createNamespaceMutation({
  key: ["qz.printers.getStatus"],
  run: async () => {
    if (!printerStore.getSnapshot().isListening) return;

    await retryOnTransientError(() => qz.printers.getStatus());
    markPrinterStatusRefresh();
  },
  store: printerStore,
});
